// src/components/ui/Toast.tsx
import { useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { colors } from '../../utils/theme';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  /** Auto-dismiss after this many ms. Default: 4000. */
  duration?: number;
}

// ── Singleton event bus (avoids context overhead) ────────────────
type ToastListener = (toast: ToastItem) => void;
let _listener: ToastListener | null = null;
/** Toasts queued before ToastContainer mounts — flushed on mount. */
const _queue: ToastItem[] = [];

function generateId() {
  return Math.random().toString(36).slice(2, 9);
}

/** Call this anywhere in the app to show a toast notification. */
export function showToast(message: string, variant: ToastVariant = 'info', duration = 4000) {
  const toast: ToastItem = { id: generateId(), message, variant, duration };
  if (_listener) {
    _listener(toast);
  } else {
    _queue.push(toast);
  }
}

// ── Toast container (mount once in Layout or App) ────────────────
export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    _listener = (toast) => {
      setToasts(prev => [...prev, toast]);
      setTimeout(() => dismiss(toast.id), toast.duration ?? 4000);
    };
    // Flush any toasts queued before this container mounted
    _queue.splice(0).forEach(t => _listener!(t));
    return () => { _listener = null; };
  }, [dismiss]);

  if (toasts.length === 0) return null;

  return (
    // UX-1 (audit Lenny 2026-07-06): en móvil (<640px) los toasts van ARRIBA
    // y a ancho completo — abajo-derecha desbordaba el viewport de 375px y
    // quedaba detrás del teclado Android justo mientras el usuario escribía.
    // En sm+ se mantiene la posición original abajo-derecha.
    <div className="fixed top-4 left-4 right-4 bottom-auto sm:top-auto sm:left-auto sm:bottom-5 sm:right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}

// ── Individual Toast ─────────────────────────────────────────────
const VARIANT_STYLES: Record<ToastVariant, {
  Icon: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  varColor: string;
}> = {
  success: { Icon: CheckCircle,   varColor: 'var(--positive)' },
  error:   { Icon: XCircle,       varColor: 'var(--negative)' },
  info:    { Icon: Info,          varColor: 'var(--brand)'    },
  warning: { Icon: AlertTriangle, varColor: 'var(--warning)'  },
};

function ToastItem({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);
  const { Icon, varColor } = VARIANT_STYLES[toast.variant];

  return (
    <div
      className="pointer-events-auto toast-enter relative overflow-hidden
        flex items-center gap-3 w-full sm:w-auto sm:min-w-[280px] sm:max-w-[380px]
        rounded-xl px-4 py-3 shadow-2xl"
      style={{ background: c.card, border: `1px solid ${c.border}` }}
    >
      <Icon size={16} className="flex-shrink-0" style={{ color: varColor }} />
      <p className="text-sm flex-1 leading-snug" style={{ color: c.textNormal }}>{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Cerrar notificación"
        className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0 p-4 -m-3"
      >
        <X size={14} />
      </button>
      <div
        className="absolute bottom-0 left-0 h-[2px] opacity-60"
        style={{ background: varColor, animation: 'toast-bar 4000ms linear forwards' }}
      />
    </div>
  );
}
